import { Injectable } from '@angular/core';
import { LoginData } from 'app/models';
import { AuthorizationData } from 'app/models/auth/claimsData';
import jwt_decode from 'jwt-decode';
import { LocalStoreService } from './local-store.service';
import { OperacijeEnum } from '../constants/operacije.enum';
import { UlogeEnum } from '../constants/uloge.enum';

@Injectable()
export class TokenDataService {
    JWT_TOKEN = "JWT_TOKEN";
    APP_USER = "EGRET_USER";
    constructor(private ls: LocalStoreService) { }

    // Dekodiranje tokena ulogovanog korisnika
    getAuthorizationClaim(): any {
        const user: LoginData = this.ls.getItem(this.APP_USER);

        if (user === undefined || user == null || user.token === undefined || user.token == null) {
            return null;
        }

        const decodedToken = jwt_decode(user.token);
        return decodedToken['Authorization claim'];
    }

    getClaimData(): AuthorizationData[] {
        let decodedToken = this.getAuthorizationClaim();
        if (!!decodedToken) {
            return decodedToken.Autorizacije;
        } else {
            return null;
        }
    }

    // Da li korisnik ima dodeljenu rolu (korisnicku ulogu) za trenutno izabranu ustanovu/kompaniju?
    hasRoleKompanija(data: any) {
        if (data == null || data.uloge == null || data.uloge.length === 0) {
            return true;
        }

        const claims = this.getAuthorizationClaim();
        const kompanijaId = Number(this.ls.getItem(this.ls.kompanijaId));

        if (claims == null || isNaN(kompanijaId) || kompanijaId === 0) {
            return false;
        }

        const result = [];
        const autorizacije = claims.Autorizacije.map((x: { Uloga: any; Kompanija: any; }) => ({ uloga: x.Uloga, kompanije: x.Kompanija }));

        for (const autorizacija of autorizacije) {
            if (data.uloge.includes(autorizacija.uloga.Naziv) &&
                autorizacija.kompanije.some((x: { Id: number; }) => x.Id === kompanijaId)) {
                result.push(autorizacija.uloga.Naziv);
            }
        }

        const resultDistinct = [...Array.from(new Set(result))];
        const dataUlogeDistinct = [...Array.from(new Set(data.uloge))];

        if ((data.mustMatchAll === true || data.mustMatchAll == null) && resultDistinct.length === dataUlogeDistinct.length) {
            return true;
        } else if (data.mustMatchAll === false && resultDistinct.length > 0) {
            return true;
        } else {
            return false;
        }
    }

    hasRole(data: any) {
        if (data == null || data.length == 0) {
            return false;
        }
        const claims = this.getAuthorizationClaim();
        const autorizacije = claims.Autorizacije.map((x: { Uloga: any }) => ({ uloga: x.Uloga }));
        const result = [];
        for (const autorizacija of autorizacije) {
            if (data.uloge.includes(autorizacija.uloga.Naziv))
                result.push(autorizacija.uloga.Naziv);
        }

        const resultDistinct = [...Array.from(new Set(result))];
        const dataUlogeDistinct = [...Array.from(new Set(data.uloge))];

        if ((data.mustMatchAll === true || data.mustMatchAll == null) && resultDistinct.length === dataUlogeDistinct.length) {
            return true;
        } else if (data.mustMatchAll === false && resultDistinct.length > 0) {
            return true;
        } else {
            return false;
        }
    }

    daLiKorisnikImaOperacijuZaOrgan(sifraOrgana: string, operacija: OperacijeEnum) {
        const autorizacije = this.getClaimData();
        let autorizacijeZaOrgan = autorizacije.filter(x => x.Kompanija?.find(k => k.Sifra == sifraOrgana) !== undefined);
        if (autorizacijeZaOrgan?.length) {
            if (autorizacijeZaOrgan.find(a => a.Uloga?.Operacije?.find(o => o.Naziv == operacija))) {
                return true;
            }
        }
        return false;
    }

    /**
     * Da li korisnik ima ulogu Administrator metaregistra.
     * Administrator metaregistra moze da vidi i menja sve Sluzbene evidencije,
     * administrira recnik i klasifikacije.
     * @returns boolean
     */
    daLiJeKorisnikAdminMetaregistra(): boolean {
        const autorizacije = this.getClaimData();
        let adminAuth = autorizacije?.find(a => a.Uloga?.Naziv == UlogeEnum.ADMINISTRATOR_METAREGISTRA);
        if (adminAuth) {
            return true;
        }
        return false;
    }

    /**
     * Da li korisnik ima pravo da izvrsi odredjenu operaciju nad sluzbenom evidecijom koja
     * pripada organu sa sifrom organSifra.
     * Ako je administrator metaregistra moze sve da radi.
     * Ako ima autorizaciju koja ima ulogu sa operacijom koja ima taj naziv, 
     * i koja ima organ sa tom sifrom - onda sme da izvrsi akciju, u suprotnom ne sme.
     */
    daLiKorisnikImaPravoNadOrganom(operacija: string, organSifra: string): boolean {
        if (this.daLiJeKorisnikAdminMetaregistra()) return true;
        const autorizacije = this.getClaimData();
        if (autorizacije?.find(a =>
            a.Kompanija.find(k => k.Sifra == organSifra) &&
            a.Uloga.Operacije.find(o => o.Naziv == operacija))
        ) {
            return true;
        }
        return false;
    }

    /**
     * Proverava da li korisnik sme da uopste vidi sluzbenu evidenciju.
     * Ako ima ulogu Administrator metaregistra sme. Ako nema, proverava se da li
     * korisnik ima ulogu Administrator organa za metaregistar za organ sa sifrom organSifra.
     * @param organSifra 
     * @returns 
     */
    daLiKorisnikVidiSluzbenuEvidenciju(organSifra: string): boolean {
        if (this.daLiJeKorisnikAdminMetaregistra()) return true;
        const autorizacije = this.getClaimData();
        if (autorizacije?.find(a =>
            a.Uloga?.Naziv == UlogeEnum.ADMINISTRATOR_ORGANA_ZA_METAREGISTAR &&
            a.Kompanija?.find(o => o.Sifra == organSifra)
        )) {
            return true;
        }
        return false;
    }

    /**
     * Da li korisnik ima potrebnu operaciju za prikaz/izvrsenje neke akcije/menija
     */ 
    hasClaim(data: { operacije: string[], mustMatchAll?: boolean }) {
        if (data == null || data.operacije == null || data.operacije.length === 0) {
            return true;
        }

        const result = [];
        const autorizacije = this.getClaimData();

        for (let uloga of autorizacije.map(x => x.Uloga)) {
            for (let operacija of uloga.Operacije) {
                if (data.operacije.includes(operacija.Naziv)) {
                    result.push(operacija.Naziv);
                }
            }
        }

        const resultDistinct = [...Array.from(new Set(result))];
        const dataOperacijeDistinct = [...Array.from(new Set(data.operacije))];

        if ((data.mustMatchAll === true || data.mustMatchAll == null) && resultDistinct.length === dataOperacijeDistinct.length) {
            return true;
        } else if (data.mustMatchAll === false && resultDistinct.length > 0) {
            return true;
        }

        return false;
    }

    hasNotClaim(data: any) {
        if (data == null || data.operacije == null || data.operacije.length === 0) {
            return false;
        }

        const claims = this.getAuthorizationClaim();
        const kompanijaId = Number(this.ls.getItem(this.ls.kompanijaId));

        if (claims == null || isNaN(kompanijaId) || kompanijaId === 0) {
            return true;
        }

        const result = [];
        const autorizacije = claims.Autorizacije.map((x: { Uloga: any; Kompanija: any; }) => ({ uloga: x.Uloga, kompanije: x.Kompanija }));

        for (const autorizacija of autorizacije) {
            for (const operacija of autorizacija.uloga.Operacije) {
                if (data.operacije.includes(operacija.Naziv) &&
                    autorizacija.kompanije.some((x: { Id: number; }) => x.Id === kompanijaId)) {
                    result.push(operacija.Naziv);
                }
            }
        }

        const resultDistinct = [...Array.from(new Set(result))];
        const dataOperacijeDistinct = [...Array.from(new Set(data.operacije))];

        if ((data.mustMatchAll === true || data.mustMatchAll == null) && resultDistinct.length === dataOperacijeDistinct.length) {
            return false;
        } else if (data.mustMatchAll === false && resultDistinct.length > 0) {
            return false;
        }

        return true;
    }

    hasClaimSekcijaOperacija(data: any): any[] {
        if (data == null || data.length === 0) {
            return null;
        }
        const claims = this.getAuthorizationClaim();
        const kompanijaId = Number(this.ls.getItem(this.ls.kompanijaId));

        if (claims == null || isNaN(kompanijaId) || kompanijaId === 0) {
            return null;
        }

        const result = [];
        const autorizacije = claims.Autorizacije.map((x: { Uloga: any; Kompanija: any; }) => ({ uloga: x.Uloga, kompanije: x.Kompanija }));

        for (const autorizacija of autorizacije) {
            for (const operacija of autorizacija.uloga.Operacije) {
                if (data.includes(operacija.Naziv) &&
                    autorizacija.kompanije.some((x: { Id: number; }) => x.Id === kompanijaId)) {
                    result.push(operacija.Naziv);
                }
            }
        }

        return result;
    }
}
